import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StopIcon,
  ArrowPathIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useKGImportProgressContext } from '../../contexts/KGImportProgressContext';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { KG_PROGRESS_MESSAGES, KG_ACTIONS, KG_METRICS_LABELS } from '../../constants/kgMessages';

/**
 * Global KG Import Indicator that shows when any KG import is active.
 * This component is displayed at the top of the screen regardless of
 * which page the user is currently on, ensuring they never lose track
 * of their import progress.
 */
export const KGImportGlobalIndicator: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAutoDismissCountdown, setShowAutoDismissCountdown] = useState(false);
  const [autoDismissSeconds, setAutoDismissSeconds] = useState(10);
  const navigate = useNavigate();

  const {
    activeJobId,
    progressData,
    isPolling: _isPolling,
    isRunning,
    isCompleted,
    isFailed,
    isCancelled,
    progressPercentage,
    currentStep,
    documentsInfo,
    performanceInfo,
    error,
    isDismissed,
    isSuppressed,
    cancelImport,
    dismissNotification,
    retryImport
  } = useKGImportProgressContext();

  // Auto-dismiss countdown for completed/failed/cancelled states
  useEffect(() => {
    if ((isCompleted || isFailed || isCancelled) && !isDismissed && !isExpanded) {
      setShowAutoDismissCountdown(true);
      setAutoDismissSeconds(10);

      const countdown = setInterval(() => {
        setAutoDismissSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            // Defer dismissNotification to avoid state update during render
            setTimeout(() => dismissNotification(), 0);
            setShowAutoDismissCountdown(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(countdown);
        setShowAutoDismissCountdown(false);
      };
    } else {
      setShowAutoDismissCountdown(false);
    }
  }, [isCompleted, isFailed, isCancelled, isDismissed, isExpanded, dismissNotification]);

  // Auto-dismiss immediately when import completes successfully
  useEffect(() => {
    if (isCompleted && !isDismissed) {
      dismissNotification();
    }
  }, [isCompleted, isDismissed, dismissNotification]);

  // Don't show anything if no active import, dismissed, completed, or suppressed by modal
  if (!activeJobId || !progressData || isDismissed || isSuppressed || isCompleted) {
    return null;
  }

  const handleViewDetails = () => {
    navigate('/admin/kg-import');
  };

  const handleCancel = async () => {
    await cancelImport();
  };

  const handleDismiss = () => {
    dismissNotification();
  };

  const handleRetry = () => {
    retryImport();
  };

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
    // Stop auto-dismiss when expanded
    if (!isExpanded) {
      setShowAutoDismissCountdown(false);
    }
  };

  const getStatusColor = () => {
    if (isCompleted) return 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-200';
    if (isFailed) return 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-200';
    if (isCancelled) return 'bg-gray-100 border-gray-300 text-gray-800 dark:bg-dark-background-secondary dark:border-border-dark dark:text-dark-text';
    return 'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-200';
  };

  const getStatusIcon = () => {
    if (isCompleted) return <CheckCircleIcon className='h-4 w-4' />;
    if (isFailed) return <ExclamationTriangleIcon className='h-4 w-4' />;
    if (isCancelled) return <XMarkIcon className='h-4 w-4' />;
    return <LoadingSpinner size='sm' />;
  };

  const getStatusText = () => {
    if (isCompleted) return KG_PROGRESS_MESSAGES.importCompleted;
    if (isFailed) return KG_PROGRESS_MESSAGES.importFailed;
    if (isCancelled) return KG_PROGRESS_MESSAGES.importCancelled;
    return KG_PROGRESS_MESSAGES.importRunning;
  };

  return (
    <div className='fixed left-0 right-0 top-16 z-50 mx-4 md:mx-8'>
      <div className={`rounded-lg border-2 shadow-lg ${getStatusColor()}`}>
        {/* Collapsed View */}
        <div className='p-4'>
          <div className='flex items-center justify-between'>
            <div className='flex min-w-0 flex-1 items-center space-x-3'>
              {getStatusIcon()}
              <div className='min-w-0 flex-1'>
                <div className='flex items-center space-x-2'>
                  <h3 className='text-sm font-semibold md:text-base'>{getStatusText()}</h3>
                  {isRunning && <span className='rounded-full bg-white bg-opacity-50 px-2 py-1 text-xs'>{Math.round(progressPercentage)}%</span>}
                </div>
                {isRunning && (
                  <div className='mt-2 h-2 w-full rounded-full bg-white bg-opacity-30'>
                    <div
                      className='h-2 rounded-full bg-current transition-all duration-500'
                      style={{ width: `${Math.max(progressPercentage, 2)}%` }}
                    />
                  </div>
                )}
                <p className='mt-1 truncate text-xs'>
                  {currentStep && `${KG_PROGRESS_MESSAGES.currentStep}: ${currentStep}`}
                  {documentsInfo && ` • ${documentsInfo.processed}/${documentsInfo.total} ${KG_PROGRESS_MESSAGES.documentsProcessed}`}
                  {performanceInfo?.nodesCreated && ` • ${performanceInfo.nodesCreated.toLocaleString()} nodes`}
                </p>
                {/* Auto-dismiss countdown */}
                {showAutoDismissCountdown && (
                  <p className='mt-1 flex items-center text-xs opacity-75'>
                    <InformationCircleIcon className='mr-1 h-3 w-3' />
                    {KG_PROGRESS_MESSAGES.autoDismiss} {autoDismissSeconds} {KG_PROGRESS_MESSAGES.seconds}
                  </p>
                )}
              </div>
            </div>

            <div className='ml-4 flex flex-shrink-0 items-center space-x-2'>
              {/* Primary Action Buttons */}
              {isRunning && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleCancel}
                  className='border-red-400 text-xs text-red-600 hover:bg-red-50 dark:border-red-500 dark:text-red-400 dark:hover:bg-rose-400/50'
                >
                  <StopIcon className='mr-1 h-3 w-3' />
                  <span className='hidden md:inline'>{KG_ACTIONS.cancel}</span>
                </Button>
              )}

              {isFailed && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleRetry}
                  className='border-blue-400 text-xs text-blue-600 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-sky-400/50'
                >
                  <ArrowPathIcon className='mr-1 h-3 w-3' />
                  <span className='hidden md:inline'>{KG_ACTIONS.retry}</span>
                </Button>
              )}

              <Button variant='outline' size='sm' onClick={handleViewDetails} className='border-current text-xs'>
                <EyeIcon className='mr-1 h-3 w-3' />
                <span className='hidden sm:inline'>{KG_ACTIONS.viewDetails}</span>
              </Button>

              {/* Expand/Collapse Button */}
              <button onClick={handleExpandToggle} className='rounded p-1 transition-colors hover:bg-white hover:bg-opacity-20'>
                {isExpanded ? <ChevronUpIcon className='h-4 w-4' /> : <ChevronDownIcon className='h-4 w-4' />}
              </button>

              {/* Dismiss Button */}
              <button
                onClick={handleDismiss}
                className='rounded p-1 transition-colors hover:bg-red-100 dark:hover:bg-red-900/30'
                title={KG_ACTIONS.dismiss}
              >
                <XMarkIcon className='h-4 w-4' />
              </button>
            </div>
          </div>
        </div>

        {/* Expanded View */}
        {isExpanded && (
          <div className='border-t border-current border-opacity-20 px-4 pb-4'>
            <div className='mt-3 grid grid-cols-2 gap-3 text-xs md:grid-cols-4'>
              {documentsInfo && (
                <div className='rounded bg-white bg-opacity-50 p-2 dark:bg-dark-background-tertiary dark:bg-opacity-70'>
                  <div className='font-semibold dark:text-dark-text'>{KG_METRICS_LABELS.documents}</div>
                  <div className='dark:text-dark-text'>
                    {documentsInfo.processed}/{documentsInfo.total}
                  </div>
                </div>
              )}

              {performanceInfo && performanceInfo.nodesCreated > 0 && (
                <div className='rounded bg-white bg-opacity-50 p-2 dark:bg-dark-background-tertiary dark:bg-opacity-70'>
                  <div className='font-semibold dark:text-dark-text'>{KG_METRICS_LABELS.nodes}</div>
                  <div className='dark:text-dark-text'>{performanceInfo.nodesCreated.toLocaleString()}</div>
                </div>
              )}

              {performanceInfo && performanceInfo.relationshipsCreated > 0 && (
                <div className='rounded bg-white bg-opacity-50 p-2 dark:bg-dark-background-tertiary dark:bg-opacity-70'>
                  <div className='font-semibold dark:text-dark-text'>{KG_METRICS_LABELS.relationships}</div>
                  <div className='dark:text-dark-text'>{performanceInfo.relationshipsCreated.toLocaleString()}</div>
                </div>
              )}

              {performanceInfo && (
                <div className='rounded bg-white bg-opacity-50 p-2 dark:bg-dark-background-tertiary dark:bg-opacity-70'>
                  <div className='font-semibold dark:text-dark-text'>{KG_METRICS_LABELS.time}</div>
                  <div className='dark:text-dark-text'>
                    {Math.floor(performanceInfo.elapsedTime / 60)}m {Math.floor(performanceInfo.elapsedTime % 60)}s
                  </div>
                </div>
              )}
            </div>

            {currentStep && (
              <div className='mt-3 text-xs'>
                <div className='mb-1 font-semibold dark:text-dark-text'>{KG_PROGRESS_MESSAGES.currentStep}:</div>
                <div className='rounded bg-white bg-opacity-50 p-2 dark:bg-dark-background-tertiary dark:bg-opacity-70 dark:text-dark-text'>{currentStep}</div>
              </div>
            )}

            {error && (
              <div className='mt-3 text-xs'>
                <div className='mb-1 font-semibold text-red-700 dark:text-red-300'>{KG_PROGRESS_MESSAGES.error}:</div>
                <div className='rounded border border-red-200 bg-red-50 p-2 text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'>
                  {error}
                </div>
                {isFailed && (
                  <div className='mt-2 flex space-x-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleRetry}
                      className='border-blue-400 text-xs text-blue-600 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-sky-400/50'
                    >
                      <ArrowPathIcon className='mr-1 h-3 w-3' />
                      {KG_ACTIONS.retry}
                    </Button>
                    <Button variant='outline' size='sm' onClick={handleViewDetails} className='border-current text-xs'>
                      <EyeIcon className='mr-1 h-3 w-3' />
                      {KG_ACTIONS.viewDetails}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions for completed imports */}
            {isCompleted && (
              <div className='mt-3 text-xs'>
                <div className='mb-2 font-semibold text-green-700 dark:text-green-300'>{KG_PROGRESS_MESSAGES.quickActions}:</div>
                <div className='flex space-x-2'>
                  <Button variant='outline' size='sm' onClick={handleViewDetails} className='border-current text-xs'>
                    <EyeIcon className='mr-1 h-3 w-3' />
                    {KG_ACTIONS.viewDetails}
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handleDismiss}
                    className='border-gray-400 text-xs text-gray-600 hover:bg-gray-50 dark:border-border-dark dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                  >
                    <CheckCircleIcon className='mr-1 h-3 w-3' />
                    {KG_ACTIONS.markAsViewed}
                  </Button>
                </div>
              </div>
            )}

            <div className='mt-3 text-xs opacity-75 dark:text-dark-text-secondary'>Job ID: {activeJobId.slice(0, 8)}...</div>
          </div>
        )}
      </div>
    </div>
  );
};
